// s169 TASK-ACTION-FEEDBACK-1 — optimistic task-status updates over the cached list payload.
//
// The defect this exists for: a task action POSTed, the server recorded it in ~1s, and the UI
// showed NOTHING until the list refetch landed — so the executor clicked again, and again, each
// click a real toggle (Reade stacked 15 activity events this way on staging). Optimism removes
// the dead window; the pending/disabled state in the components removes the double-fire.
//
// Counts are recomputed with the SHARED math (`shared/executor/task-counts.ts`), never a local
// copy — an optimistic counter that disagreed with the server's would be a worse bug than the
// lag it replaced.

import { stageCounts, summarizeTasks, type CountableTask } from "@shared/executor/task-counts";

/** Structural minimum this module needs. Mirrors the wire shape without importing the page. */
interface OptimisticTask extends CountableTask {
  taskKey: string;
  recordRowId?: string | null;
}
interface OptimisticStage<T extends OptimisticTask> {
  tasks: T[];
  counts: { total: number; done: number; autoResolved: number; dismissed: number };
}
interface OptimisticPayload<T extends OptimisticTask> {
  stages: OptimisticStage<T>[];
  summary: { totalTasks: number; doneTasks: number; criticalRemaining: number; autoResolved: number; dismissed: number };
}

/**
 * Identity for a task ROW. s169 Piece 2: a promoted per-record row shares its taskKey with its
 * siblings, so the pair is the identity — matching on taskKey alone would flip every sibling at
 * once, which is the same class of bug the write path had at the API boundary.
 */
function sameRow(t: OptimisticTask, taskKey: string, recordRowId: string | null): boolean {
  return t.taskKey === taskKey && (t.recordRowId ?? null) === recordRowId;
}

/**
 * Return a NEW payload with the named row's status flipped and every affected count recomputed.
 * Returns the input unchanged (same reference) when the row isn't present, so a caller can safely
 * apply this to a stale cache without inventing rows.
 *
 * Note what is deliberately NOT done: stages are never added or removed. A status change cannot
 * empty a stage (the row stays in `tasks`, it just leaves the ACTIVE subset), so the server's
 * "drop stages with no tasks" filter has nothing to mirror here.
 */
export function applyOptimisticTaskStatus<T extends OptimisticTask>(
  data: OptimisticPayload<T>,
  taskKey: string,
  recordRowId: string | null,
  status: string,
): OptimisticPayload<T> {
  let touched = false;

  const stages = data.stages.map((stage) => {
    if (!stage.tasks.some((t) => sameRow(t, taskKey, recordRowId))) return stage;
    touched = true;
    const tasks = stage.tasks.map((t) => (sameRow(t, taskKey, recordRowId) ? { ...t, status } : t));
    return { ...stage, tasks, counts: stageCounts(tasks) };
  });

  if (!touched) return data;

  return { ...data, stages, summary: summarizeTasks(stages.flatMap((s) => s.tasks)) };
}
